import { creditNoteService, talentService, emailService } from '../../../api/apiService';
import { buildCreditNoteEmailHtml } from './creditNoteEmailTemplate';

// Convert a Blob to a base64 string (strips the data-URL prefix)
export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob instanceof Blob ? blob : new Blob([blob]));
  });

/**
 * Send credit note notification emails for a list of freshly created credit notes.
 * Shared by the confirmed-credit-notes page and the confirmed-orders list, so both
 * entry points send the exact same Gutschrift email (PDF + XML attached).
 *   - Fetch full credit note to get talent_id
 *   - Fetch talent by ID to get email address (talent.email || talent.E_Mail)
 *   - to_email is an array: [talentEmail, 'talents@set-cast.com']
 *   - PDF and XML attached as base64 / utf-8
 *
 * Never throws — logs and continues, so one failed email can't abort a batch.
 *
 * @param {Array} createdNotes - Items from API `data.created` — each needs at least `id`
 */
export const sendCreditNoteEmails = async (createdNotes) => {
  for (const cnSummary of createdNotes) {
    try {
      // 1. Fetch full credit note to get talent_id and all details for the template
      const cnRes = await creditNoteService.getCreditNoteById(cnSummary.id);
      const cn = cnRes?.data?.creditNote || cnRes?.data || cnSummary;
      const noteNumber = cn.credit_note_number || cn.creditNoteNumber || cn.id;

      // 2. Fetch talent by ID to get the recipient email address
      const talentId = cn.talent_id || cn.talentId;
      if (!talentId) {
        console.warn(`No talent_id on CN ${noteNumber}, skipping email.`);
        continue;
      }

      const talentRes = await talentService.getTalentById(talentId);
      const talent = talentRes?.data?.talent || talentRes?.data;
      const talentEmail = talent?.email || talent?.E_Mail;

      if (!talentEmail) {
        console.warn(`No email on talent ${talentId} for CN ${noteNumber}, skipping email.`);
        continue;
      }

      // 3. Fetch PDF and XML in parallel
      const [pdfRes, xmlRes] = await Promise.all([
        creditNoteService.getCreditNotePdf(cn.id || cnSummary.id),
        creditNoteService.getCreditNoteXml(cn.id || cnSummary.id),
      ]);

      const pdfBase64 = await blobToBase64(pdfRes.data);
      const xmlContent = typeof xmlRes.data === 'string' ? xmlRes.data : String(xmlRes.data);

      // 4. Send — Gutschrift PDF + XML attached
      await emailService.sendEmail({
        to_email: [talentEmail, 'talents@set-cast.com'],
        subject: `Credit Note ${noteNumber} – Set-Cast`,
        content: buildCreditNoteEmailHtml(cn),
        attachments: [
          {
            filename: `Gutschrift_${noteNumber}.pdf`,
            content: pdfBase64,
            encoding: 'base64',
            contentType: 'application/pdf',
          },
          {
            filename: `Gutschrift_${noteNumber}.xml`,
            content: xmlContent,
            encoding: 'utf-8',
            contentType: 'application/xml',
          },
        ],
      });
    } catch (err) {
      console.error(`Email failed for CN ${cnSummary.creditNoteNumber || cnSummary.id}:`, err);
      // Don't throw — continue with remaining notes
    }
  }
};
